import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './BoardOfDirectors.module.css';
import API_BASE_URL from '../../config/api';

const BoardOfDirectors = () => {
  const { t, i18n } = useTranslation();
  const [boardMembers, setBoardMembers] = useState([]);

  useEffect(() => {
    const controller = new AbortController();

    const fetchMembers = async () => {
      try {
        const lang = i18n.language === 'ar' ? 'ar' : 'en';
        const res = await fetch(
          `${API_BASE_URL}/board-members?lang=${lang}`,
          { signal: controller.signal },
        );
        const data = await res.json();

        const items = Array.isArray(data) ? data : data.data || [];

        setBoardMembers(
          items.map((item) => ({
            name: item.name,
            position: item.position,
            phones: item.phones || [],
            emails: item.emails || [],
            image: item.image_url || null,
          })),
        );
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error('Failed to load board members', error);
        }
      }
    };

    fetchMembers();

    return () => controller.abort();
  }, [i18n.language]);

  return (
    <div className={styles.boardPage}>
      {/* Hero Section */}
      <section className={styles.heroSection}>
        <div className={styles.heroOverlay}></div>
        <div className={styles.heroContent}>
          <h1 className={styles.heroTitle}>{t('board.title')}</h1>
          <div className={styles.heroUnderline}></div>
        </div>
      </section>

      {/* Board Members Grid */}
      <section className={styles.membersSection}>
        <div className={styles.container}>
          <div className={styles.membersGrid}>
            {boardMembers.map((member, index) => (
              <div key={index} className={styles.memberCard}>
                {/* Header with Image */}
                <div className={styles.cardHeader}>
                  {member.image ? (
                    <img 
                      src={member.image} 
                      alt={member.name}
                      className={styles.headerImage}
                    />
                  ) : (
                    <div className={styles.headerPlaceholder}>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                        <circle cx="12" cy="7" r="4" />
                      </svg>
                    </div>
                  )}
                </div>

                {/* Card Body */}
                <div className={styles.cardBody}>
                  <h3 className={styles.memberName}>{member.name}</h3>
                  <div className={styles.positionBadge}>{member.position}</div>
                  
                  <div className={styles.divider}></div>

                  <div className={styles.contactSection}>
                    {member.phones && member.phones.length > 0 && (
                      <div className={styles.contactGroup}>
                        <div className={styles.contactIcon}>
                          <svg viewBox="0 0 20 20" fill="currentColor">
                            <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
                          </svg>
                        </div>
                        <div className={styles.contactList}>
                          {member.phones.map((phone, phoneIndex) => (
                            <a key={phoneIndex} href={`tel:${phone.replace(/\s/g, '')}`} className={styles.contactItem}>
                              {phone}
                            </a>
                          ))}
                        </div>
                      </div>
                    )}

                    {member.emails && member.emails.length > 0 && (
                      <div className={styles.contactGroup}>
                        <div className={styles.contactIcon}>
                          <svg viewBox="0 0 20 20" fill="currentColor">
                            <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                            <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                          </svg>
                        </div>
                        <div className={styles.contactList}>
                          {member.emails.map((email, emailIndex) => (
                            <a key={emailIndex} href={`mailto:${email}`} className={styles.contactItem}>
                              {email}
                            </a>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default BoardOfDirectors;
